import dayjs from 'lib/dayjs'
import { createContext, useContext, useMemo, useState } from 'react'
import {
  DefaultFilterParams,
  PDFSettings,
  ReportFiltersContextProps,
} from './ReportFiltersContext.types'

export const initialBefore = dayjs(dayjs().endOf('day').toDate())
export const initialAfter = dayjs().startOf('day').subtract(1, 'day')

const ReportFiltersContext = createContext<ReportFiltersContextProps>({
  filters: {
    open: false,
  },
  setFilters: () => {},
  beforeDate: initialBefore,
  afterDate: initialAfter,
  setBeforeDate: () => {},
  setAfterDate: () => {},
  reportData: [],
  setReportData: () => {},
  pdfSettings: {
    selectedAssetTypes: [
      'Implantable Biologic',
      'Implantable Hardware/Associated Product',
      'Implantable Other',
    ],
  },
  setPDFSettings: () => {},
})

interface ReportFiltersProviderProps {
  children: React.ReactNode
}

export const ReportFiltersProvider = ({
  children,
}: ReportFiltersProviderProps) => {
  const [beforeDate, setBeforeDate] = useState<dayjs.Dayjs>(initialBefore)
  const [afterDate, setAfterDate] = useState<dayjs.Dayjs>(initialAfter)
  const [pdfSettings, setPDFSettings] = useState<PDFSettings>({
    selectedAssetTypes: [
      'Implantable Biologic',
      'Implantable Hardware/Associated Product',
      'Implantable Other',
    ],
  })

  const [filters, setFilters] = useState<DefaultFilterParams>({
    open: false,
  })

  const [reportData, setReportData] = useState<
    ReportFiltersContextProps['reportData']
  >([])

  const value = useMemo(() => {
    return {
      filters,
      setFilters,
      beforeDate,
      afterDate,
      setBeforeDate,
      setAfterDate,
      reportData,
      setReportData,
      pdfSettings,
      setPDFSettings,
    }
  }, [afterDate, beforeDate, filters, pdfSettings, reportData])

  return (
    <ReportFiltersContext.Provider value={value}>
      {children}
    </ReportFiltersContext.Provider>
  )
}

export function useReportFiltersContext() {
  const context = useContext(ReportFiltersContext)
  if (context === undefined) {
    throw new Error(
      'useReportFiltersContext must be used with ReportFiltersProvider'
    )
  }
  return context
}
